import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail } from 'lucide-react';
import { apiClient, getErrorMessage } from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { AuthLayout, FormError } from '@/components/auth/AuthLayout';
import HumanCheck, { HumanCheckRef, needsHumanCheck } from '@/components/auth/HumanCheck';

const schema = z.object({ email: z.string().email('Enter a valid email address') });
type FormData = z.infer<typeof schema>;

const ForgotPasswordPage: React.FC = () => {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  // The button stays off until the invisible browser check hands back a token.
  const waitingForCheck = needsHumanCheck && !token;
  const check = useRef<HumanCheckRef>(null);
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async ({ email }: FormData) => {
    setError(null);
    if (waitingForCheck) {
      setError('The browser check is still running. Try again in a moment.');
      return;
    }
    setBusy(true);
    try {
      // The gateway answers the same way whether or not the email has an account.
      await apiClient.post('/auth/password/reset', { email, client: 'console', turnstileToken: token ?? undefined });
      setSent(true);
    } catch (err) {
      setError(getErrorMessage(err));
      check.current?.reset();
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout title="Check your email" subtitle="If an account exists for that address, a link to reset the password is on its way. It works once and expires in 1 hour.">
        <Link to="/login" className="text-sm font-medium text-accent-text hover:underline">Back to sign in</Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset your password" subtitle="Enter the email you signed up with and we'll send you a link.">
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Input label="Email address" type="email" autoComplete="email" leftIcon={<Mail className="h-4 w-4" />} error={errors.email?.message} {...register('email')} />
        <HumanCheck ref={check} onToken={setToken} />
        <FormError message={error} />
        <Button type="submit" size="lg" loading={busy} disabled={waitingForCheck} className="w-full">Send reset link</Button>
        <p className="text-center text-sm text-muted-foreground">
          Remembered it? <Link to="/login" className="font-medium text-accent-text hover:underline">Sign in</Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
